import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileCheck, CheckCircle, ArrowRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const PAGES = {
  ubl: {
    slug: "/ubl",
    tab: "ubl",
    metaTitle: "Validateur UBL 2.1 en ligne gratuit — Conformité Peppol & norme 2026 (XSD + BR-FR V1.4.0)",
    metaDescription: "Validez gratuitement vos e-invoices XML UBL 2.1 (Invoice, CreditNote, Order) : schéma XSD OASIS, conformité Peppol/EN16931 et règles françaises BR-FR V1.4.0 de la norme AFNOR XP Z12-012 du 30/06/2026. Alternative gratuite à ecosio.",
    h1: "Validez vos factures UBL 2.1 pour la réforme 2026",
    intro: "UBL (Universal Business Language) est le format XML de facture normalisé par OASIS et retenu dans le socle minimal de la réforme française de facturation électronique, aux côtés de CII et Factur-X. Dès le 1er septembre 2026, toute facture B2B domestique devra circuler dans l'un de ces formats via une Plateforme Agréée.",
    sections: [
      {
        title: "Le format UBL dans la réforme française",
        paragraphs: [
          "UBL 2.1 définit des documents XML standardisés : Invoice (facture), CreditNote (avoir) et Order (commande). C'est le format le plus répandu en Europe, notamment via le réseau Peppol, et l'une des trois syntaxes du socle minimal français.",
          "Pour être conforme à la réforme, une facture UBL doit respecter trois niveaux : la structure XSD OASIS UBL 2.1, la sémantique européenne EN16931, et les règles de gestion additionnelles françaises BR-FR-CTC définies par la norme AFNOR XP Z12-012 du 30/06/2026 (SIREN du vendeur et de l'acheteur, catégories et taux de TVA français, mentions obligatoires, cadre de facturation, option de TVA d'après les débits…)."
        ]
      },
      {
        title: "Ce que vérifie notre validateur",
        bullets: [
          "Bonne formation XML (well-formedness)",
          "Conformité au schéma XSD officiel OASIS UBL 2.1 (Invoice, CreditNote, Order)",
          "Règles de gestion françaises BR-FR V1.4.0 (Schematron officiel FNFE-MPE, norme du 30/06/2026)",
          "Rapport détaillé téléchargeable : règle en échec, message explicatif, élément XML concerné",
          "Pour les supporters : contrôle de cohérence IA (recalcul des lignes, totaux HT/TTC, TVA, dates) et vérification des SIREN/SIRET dans l'annuaire des entreprises"
        ]
      },
      {
        title: "Erreurs fréquentes sur les factures UBL",
        bullets: [
          "SIREN du vendeur absent ou invalide (cbc:CompanyID avec schemeID « 0002 », 9 chiffres)",
          "EndpointID (adresse de facturation électronique) manquant pour le vendeur ou l'acheteur",
          "Incohérence entre la somme des lignes, le total HT et le total TTC",
          "Taux de TVA non conforme aux taux légaux français (20 %, 10 %, 5,5 %, 2,1 %, 0 %)",
          "Code de type de facture (InvoiceTypeCode) non autorisé par le cadre de facturation français"
        ]
      }
    ],
    faq: [
      { q: "UBL est-il obligatoire en 2026 ?", a: "Non : UBL est l'un des trois formats du socle minimal (avec CII et Factur-X). Toute entreprise assujettie à la TVA devra pouvoir recevoir des factures électroniques au 1er septembre 2026, dans le format choisi par sa Plateforme Agréée." },
      { q: "Quels documents UBL sont pris en charge ?", a: "Invoice (facture), CreditNote (avoir) et Order (commande), au format UBL 2.1 (OASIS). La validation BR-FR s'applique aux factures et avoirs." },
      { q: "Quelle version des règles françaises appliquez-vous ?", a: "Le Schematron officiel BR-FR-Flux2-UBL V1.4.0 publié le 30/06/2026 par le FNFE-MPE en application de la norme AFNOR XP Z12-012 (norme 2026)." },
      { q: "UBL fonctionne-t-il avec Peppol ?", a: "Oui : UBL 2.1 est la syntaxe du standard Peppol BIS Billing 3.0, utilisé dans toute l'Europe pour l'e-invoicing. Une facture UBL conforme EN16931 et aux règles BR-FR est également une excellente base pour vos échanges Peppol." },
      { q: "Existe-t-il une alternative gratuite à ecosio pour valider ses fichiers ?", a: "Oui : ce validateur en ligne est une alternative gratuite aux validateurs payants comme ecosio pour vérifier la conformité de vos XML UBL, CII/Factur-X et CDAR aux règles françaises BR-FR V1.4.0 et à l'EN16931 — 5 validations gratuites par jour, sans inscription." },
      { q: "Ce validateur est-il gratuit ?", a: "Oui : 5 validations gratuites par jour. Les supporters (2 €) bénéficient de 1000 validations/mois, du contrôle de cohérence IA et de la vérification annuaire." }
    ],
    cta: "Valider mon fichier UBL"
  },
  facturx: {
    slug: "/factur-x",
    tab: "cii",
    metaTitle: "Valider une facture Factur-X en ligne gratuitement — E-invoice conforme norme 2026 (EN16931 + BR-FR V1.4.0)",
    metaDescription: "Validateur Factur-X gratuit : vérifiez la conformité de vos e-invoices Factur-X/CII à la norme AFNOR XP Z12-012 du 30/06/2026, au profil EN16931 et aux règles françaises BR-FR V1.4.0. Alternative gratuite à ecosio.",
    h1: "Validez vos factures Factur-X pour la réforme 2026",
    intro: "Factur-X est le format hybride franco-allemand de facture électronique : un PDF lisible par l'humain contenant un fichier XML structuré (CII) lisible par les machines. C'est l'un des trois formats du socle minimal de la réforme française de la facturation électronique, obligatoire dès le 1er septembre 2026 pour la réception.",
    sections: [
      {
        title: "Qu'est-ce que Factur-X ?",
        paragraphs: [
          "Factur-X (aussi connu sous le nom ZUGFeRD en Allemagne) combine un PDF/A-3 et un XML au format UN/CEFACT CII (Cross Industry Invoice). Le destinataire peut lire la facture comme un PDF classique, tandis que son logiciel comptable exploite automatiquement les données du XML embarqué.",
          "Dans le cadre de la réforme française, le XML Factur-X doit respecter le profil européen EN16931 (ou EXTENDED-CTC-FR) ainsi que les règles de gestion françaises BR-FR-CTC définies par la norme AFNOR XP Z12-012 du 30/06/2026."
        ]
      },
      {
        title: "Ce que vérifie notre validateur",
        bullets: [
          "Bonne formation XML et structure CII (CrossIndustryInvoice)",
          "Conformité au profil européen EN16931 (Schematron officiel)",
          "Règles de gestion françaises BR-FR V1.4.0 (Schematron officiel FNFE-MPE, norme du 30/06/2026) : SIREN, TVA, mentions obligatoires, cadre de facturation…",
          "Pour les supporters : contrôle de cohérence IA (calculs, totaux, TVA, dates) et vérification des SIREN/SIRET dans l'annuaire des entreprises"
        ]
      },
      {
        title: "Pourquoi valider avant d'émettre ?",
        paragraphs: [
          "À partir de septembre 2026, vos factures transiteront par des Plateformes Agréées (PA) qui rejetteront tout fichier non conforme. Valider en amont vos Factur-X vous évite les rejets, les retards de paiement et les allers-retours avec vos clients. Notre validateur applique exactement les mêmes schematrons officiels que ceux publiés par le FNFE-MPE."
        ]
      }
    ],
    faq: [
      { q: "Factur-X est-il obligatoire en 2026 ?", a: "Toutes les entreprises assujetties à la TVA devront pouvoir recevoir des factures électroniques au 1er septembre 2026. Factur-X fait partie des trois formats du socle minimal (avec UBL et CII), mais vous pouvez choisir n'importe lequel des trois." },
      { q: "Quelle est la différence entre Factur-X et CII ?", a: "Le XML embarqué dans un Factur-X EST un fichier CII. Factur-X ajoute simplement l'enveloppe PDF/A-3 lisible par l'humain. Notre validateur vérifie le XML CII, que vous l'ayez extrait d'un Factur-X ou généré directement." },
      { q: "Ce validateur est-il gratuit ?", a: "Oui : 5 validations gratuites par jour (structure XML, EN16931, règles BR-FR V1.4.0). Les supporters (2 €) bénéficient de 1000 validations/mois, du contrôle de cohérence IA et de la vérification annuaire." },
      { q: "Quelle alternative gratuite à ecosio pour valider un Factur-X ?", a: "Ce validateur en ligne constitue une alternative gratuite aux outils payants comme ecosio : il applique les mêmes schematrons officiels (EN16931 et BR-FR V1.4.0 du FNFE-MPE) pour vérifier la conformité de vos e-invoices Factur-X et CII, sans inscription." }
    ],
    cta: "Valider mon fichier Factur-X / CII"
  },
  cii: {
    slug: "/cii",
    tab: "cii",
    metaTitle: "Validateur CII (UN/CEFACT) en ligne — E-invoice XML conforme EN16931 & BR-FR V1.4.0",
    metaDescription: "Validez gratuitement vos e-invoices XML CII (Cross Industry Invoice) : conformité EN16931 et règles françaises BR-FR V1.4.0 de la norme AFNOR XP Z12-012 du 30/06/2026.",
    h1: "Validez vos factures CII (Cross Industry Invoice)",
    intro: "Le CII (Cross Industry Invoice) est le format XML de facture normalisé par l'UN/CEFACT. Il fait partie du socle minimal de la réforme française de facturation électronique 2026, aux côtés d'UBL et de Factur-X.",
    sections: [
      {
        title: "Le format CII dans la réforme française",
        paragraphs: [
          "La norme AFNOR XP Z12-012 du 30/06/2026 définit les profils et formats des messages factures pour la réforme. Le CII D22B y est retenu comme syntaxe pivot : c'est aussi le format du XML embarqué dans Factur-X.",
          "Une facture CII conforme doit respecter la sémantique européenne EN16931 (obligatoire pour toute facture B2B domestique) ainsi que les règles de gestion additionnelles françaises BR-FR-CTC : identification SIREN du fournisseur et du client, catégories et taux de TVA français, mentions obligatoires, option de paiement de la TVA d'après les débits, cadre de facturation (B2B, B2C, international)…"
        ]
      },
      {
        title: "Ce que vérifie notre validateur",
        bullets: [
          "Bonne formation XML et racine rsm:CrossIndustryInvoice",
          "Schematron EN16931-CII officiel (plus de 200 règles européennes BR, BR-CO, BR-S…)",
          "Schematron BR-FR-Flux2-CII V1.4.0 officiel FNFE-MPE (norme 2026)",
          "Rapport détaillé : règle en échec, message explicatif, XPath de l'élément concerné",
          "Pour les supporters : analyse IA des erreurs avec corrections concrètes à apporter"
        ]
      },
      {
        title: "Erreurs fréquentes sur les factures CII",
        bullets: [
          "SIREN du vendeur absent ou mal placé (schemeID 0002)",
          "Incohérence entre somme des lignes, total HT et total TTC (BR-CO-10 à BR-CO-15)",
          "Taux de TVA non conforme aux taux légaux français (20 %, 10 %, 5,5 %, 2,1 %, 0 %)",
          "Code de type de document non autorisé par le cadre de facturation français"
        ]
      }
    ],
    faq: [
      { q: "Quelle version du Schematron utilisez-vous ?", a: "La version V1.4.0 publiée le 30/06/2026 par le FNFE-MPE en application de la norme AFNOR XP Z12-012 (norme 2026), ainsi que le Schematron EN16931-CII officiel européen." },
      { q: "Puis-je valider le XML extrait d'un Factur-X ?", a: "Oui, le XML embarqué dans un Factur-X est un CII : déposez-le tel quel dans l'onglet Factur-X / CII." },
      { q: "Mes fichiers sont-ils conservés ?", a: "Les fichiers sont analysés côté serveur pour produire le rapport et l'historique de validation. Aucun fichier n'est transmis à des tiers." }
    ],
    cta: "Valider mon fichier CII"
  },
  cdar: {
    slug: "/cdar",
    tab: "cdar",
    metaTitle: "Validateur CDAR en ligne — Conformité statuts de cycle de vie e-invoice (BR-FR-CDV V1.4.0)",
    metaDescription: "Validez vos messages CDAR (Cross Domain Acknowledgement and Response) : statuts de cycle de vie de la facture électronique française, règles BR-FR-CDV V1.4.0, norme AFNOR XP Z12-012 du 30/06/2026.",
    h1: "Validez vos messages CDAR (cycle de vie des factures)",
    intro: "Le CDAR (Cross Domain Acknowledgement and Response) est le message UN/CEFACT utilisé en France pour transmettre les statuts de cycle de vie d'une facture électronique : déposée, reçue, mise à disposition, prise en charge, approuvée, paiement transmis, encaissée, refusée, en litige…",
    sections: [
      {
        title: "Le rôle du CDAR dans la réforme 2026",
        paragraphs: [
          "La réforme française ne se limite pas à l'échange de factures : chaque acteur (plateforme agréée, PPF, acheteur) doit renvoyer des statuts normalisés pour suivre le traitement de la facture et alimenter le e-reporting. Ces statuts (CDV-200 à CDV-215) sont portés par des messages CDAR au format D22B.",
          "La norme AFNOR XP Z12-012 du 30/06/2026 définit les règles de gestion BR-FR-CDV : cohérence du code statut, référence à la facture d'origine, identification des parties, motifs obligatoires pour les statuts de refus ou de litige…"
        ]
      },
      {
        title: "Ce que vérifie notre validateur",
        bullets: [
          "Bonne formation XML et structure CrossDomainAcknowledgementAndResponse (D22B)",
          "Schematron BR-FR-CDV V1.4.0 officiel FNFE-MPE (norme du 30/06/2026)",
          "Règles BR-FR-CDV et listes de codes CL (statuts CDV-200 à CDV-215, motifs, rôles)",
          "Rapport détaillé avec XPath de chaque anomalie",
          "Pour les supporters : explication IA de chaque erreur et corrections à apporter"
        ]
      },
      {
        title: "Statuts de cycle de vie couverts",
        bullets: [
          "Cas nominal : Déposée (CDV-200), Reçue (CDV-202), Mise à disposition (CDV-203), Prise en charge (CDV-204), Approuvée (CDV-205), Paiement transmis (CDV-211), Encaissée (CDV-212)",
          "Cas particuliers : Refusée, En litige (CDV-207) avec motifs obligatoires, statuts pour le PPF"
        ]
      }
    ],
    faq: [
      { q: "Qui doit émettre des CDAR ?", a: "Les Plateformes Agréées (PA) et les acheteurs, pour informer le fournisseur et l'administration du traitement de chaque facture. Les éditeurs de logiciels doivent donc savoir générer et consommer ces messages." },
      { q: "Quelle version des règles appliquez-vous ?", a: "Les règles BR-FR-CDV V1.4.0 publiées le 30/06/2026 par le FNFE-MPE en application de la norme AFNOR XP Z12-012 (norme 2026)." },
      { q: "Un avertissement invalide-t-il mon CDAR ?", a: "Non : seules les erreurs bloquantes rendent le message non conforme. Les avertissements signalent des points à vérifier." }
    ],
    cta: "Valider mon fichier CDAR"
  }
};

const setMeta = (title, description) => {
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", description);
};

export default function SeoPage({ pageKey }) {
  const page = PAGES[pageKey];
  const navigate = useNavigate();

  useEffect(() => {
    setMeta(page.metaTitle, page.metaDescription);
    window.scrollTo(0, 0);
    // FAQPage structured data (Google rich results)
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "seo-faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": page.faq.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("seo-faq-jsonld")?.remove();
      setMeta(
        "Valideur Réforme Électronique France — UBL, Factur-X/CII & CDAR",
        "Validateur gratuit de factures électroniques UBL, Factur-X/CII et de messages CDAR conforme à la norme AFNOR XP Z12-012 du 30/06/2026."
      );
    };
  }, [page]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50" data-testid={`seo-page-${pageKey}`}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="seo-home-link">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg">
              <FileCheck className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-base font-bold text-slate-800">Valideur Réforme Électronique France</p>
              <p className="text-xs text-slate-500">Norme AFNOR XP Z12-012 du 30/06/2026 • Version 2.1</p>
            </div>
          </a>
          <Button variant="outline" size="sm" onClick={() => navigate("/")} className="gap-1 border-blue-200 text-blue-600 hover:bg-blue-50">
            <ChevronLeft className="h-4 w-4" /> Validateur
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-5 leading-tight">{page.h1}</h1>
        <p className="text-base text-slate-600 leading-relaxed mb-8">{page.intro}</p>

        <Button
          onClick={() => navigate(`/?tab=${page.tab}`)}
          data-testid="seo-cta-top"
          className="mb-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-5 px-6 rounded-xl shadow-lg gap-2"
        >
          {page.cta} <ArrowRight className="h-4 w-4" />
        </Button>

        {page.sections.map((s, i) => (
          <section key={i} className="mb-10">
            <h2 className="text-lg font-semibold text-slate-800 mb-3">{s.title}</h2>
            {s.paragraphs?.map((p, j) => (
              <p key={j} className="text-sm text-slate-600 leading-relaxed mb-3">{p}</p>
            ))}
            {s.bullets && (
              <ul className="space-y-2">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Questions fréquentes</h2>
          <div className="space-y-4">
            {page.faq.map((f, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-800 mb-2">{f.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-xl font-bold mb-2">Testez la conformité de vos fichiers dès maintenant</h2>
          <p className="text-sm text-blue-100 mb-5">Gratuit — 5 validations par jour, Schematron officiel V1.4.0 (norme 2026)</p>
          <Button
            onClick={() => navigate(`/?tab=${page.tab}`)}
            data-testid="seo-cta-bottom"
            className="bg-white text-blue-700 hover:bg-blue-50 font-semibold py-5 px-6 rounded-xl gap-2"
          >
            {page.cta} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Internal links */}
        <nav className="mt-10 flex flex-wrap gap-4 text-sm text-blue-600">
          <a href="/" className="hover:underline">Validateur en ligne</a>
          <a href="/ubl" className="hover:underline">Guide UBL</a>
          <a href="/factur-x" className="hover:underline">Guide Factur-X</a>
          <a href="/cii" className="hover:underline">Guide CII</a>
          <a href="/cdar" className="hover:underline">Guide CDAR</a>
        </nav>
      </main>
    </div>
  );
}
